import React, { useCallback, useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import type { GoLiveStateDTO } from "../../data/goLiveApi";
import type { ApprovalsPreview } from "../../domain/approvals";
import type { EscalationsPreview } from "../../domain/escalations";
import type { TeamPreviewEntry } from "../../domain/team";
import { APPROVALS_PREVIEW_KEY } from "../../domain/approvals";
import { ESCALATIONS_PREVIEW_KEY } from "../../domain/escalations";
import { TEAM_PREVIEW_KEY } from "../../domain/team";
import { fetchGoLiveState, runGoLiveStep } from "../../data/goLiveApi";
import { getClient, updateClient, useCoAnswer, useCoClient } from "../../data/store";
import { GroupedSequence } from "../../components/GroupedSequence";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

/**
 * Go live — the one-way door, and the machinery behind it.
 *
 * Three faces, decided by the server's state (never by local memory):
 *
 * 1. REVIEW — nothing has run yet. A compact summary of what onboarding
 *    decided, the mock's warning, and "Confirm and go live".
 * 2. RUNNING — the go-live sequence as ConnectionSequence rows. Confirming
 *    fires `run` in a loop; each call executes ONE step server-side, so a
 *    closed tab loses nothing and Retry resumes from the failed row.
 * 3. LIVE — the tenant's URL and every login the firm hands over. This face
 *    is permanent: the door does not reopen.
 *
 * What deliberately does NOT happen at go-live (managers, the contacts
 * upload, bank rows, non-standard approval packages) is the recorded runbook
 * in docs/onboarding-tenant-data-map.md — the panel points at it rather than
 * pretending the list is empty.
 */
export function StartDateStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "start-date");

  const team = useCoAnswer<TeamPreviewEntry[]>(clientId, TEAM_PREVIEW_KEY);
  const approvals = useCoAnswer<ApprovalsPreview>(clientId, APPROVALS_PREVIEW_KEY);
  const escalations = useCoAnswer<EscalationsPreview>(clientId, ESCALATIONS_PREVIEW_KEY);

  const [state, setState] = useState<GoLiveStateDTO | undefined>();
  /** The state read/run itself could not happen — network, auth. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const alive = useRef(true);

  /**
   * Teach the local copy that the client is live, so the workspace preview's
   * "Live" dot and the board row flip without a reload. live_at and status are
   * server-owned columns — they are not in the patch map, so this only ever
   * updates memory.
   */
  const absorbLive = useCallback(
    (s: GoLiveStateDTO) => {
      if (s.client.live_at && !getClient(clientId)?.liveAt) {
        updateClient(clientId, { liveAt: s.client.live_at, status: "live" });
      }
    },
    [clientId]
  );

  useEffect(() => {
    alive.current = true;
    fetchGoLiveState(clientId)
      .then((s) => {
        if (alive.current) {
          setState(s);
          absorbLive(s);
        }
      })
      .catch((e: unknown) => {
        if (alive.current) setRefusal(e instanceof Error ? e.message : "Could not reach the server.");
      });

    return () => {
      alive.current = false;
    };
  }, [clientId, absorbLive]);

  if (!client) return null;
  const name = client.name || "this client";

  /**
   * One step per request until the sequence completes or a row fails. The
   * loop re-reads the whole state every time, so the rows on screen are
   * always the server's truth.
   */
  const launch = async () => {
    setRunning(true);
    setRefusal(null);
    try {
      for (;;) {
        const s = await runGoLiveStep(clientId);
        if (!alive.current) return;
        setState(s);
        absorbLive(s);
        if (s.complete || s.steps.some((step) => step.status === "failed")) break;
      }
    } catch (e: unknown) {
      if (alive.current) setRefusal(e instanceof Error ? e.message : "Could not reach the server.");
    } finally {
      if (alive.current) setRunning(false);
    }
  };

  // ---- Face 3: live ----
  if (state?.complete) {
    return (
      <SubStepShell
        title={`${name} is live`}
        description={`${name} is live since ${formatStamp(state.client.live_at)}. Every step finished — the workspace address and logins are below.`}
        thenWhat="Nothing else to do here. Done → back to the onboarding board."
        onContinue={() => void goNext()}
        continueLabel="Done"
      >
        <LivePanel state={state} name={name} masterAdminDomain={window.location.origin} />
      </SubStepShell>
    );
  }

  // ---- Face 2: running / failed / resumable ----
  if (state?.started || running) {
    const failed = state?.steps.some((step) => step.status === "failed") ?? false;
    const rows = (state?.steps ?? []).map((step, index, all) => ({
      ...step,
      // The first not-done row is the one the in-flight request is executing.
      status:
        running && step.status !== "done" && all.slice(0, index).every((s) => s.status === "done")
          ? ("running" as const)
          : step.status,
    }));

    // Say where the run actually is and what happens next, rather than one
    // sentence that reads the same whether it is working, stuck or waiting.
    const total = rows.length;
    const done = rows.filter((step) => step.status === "done").length;
    const runningRow = rows.find((step) => step.status === "running");
    const failedRow = rows.find((step) => step.status === "failed");

    const description = failedRow
      ? `Stopped at step ${done + 1} of ${total} — ${failedRow.label}. Nothing after it has run.`
      : runningRow
        ? `Step ${done + 1} of ${total} — ${runningRow.label}. Safe to leave this page and come back.`
        : `${done} of ${total} steps done. Nothing is running right now.`;

    const thenWhat = failedRow
      ? "Read what the failed row says, fix it, then press Try again on that row."
      : runningRow
        ? `Next: ${rows[done + 1]?.label ?? "finishing up"}.`
        : "Press Resume to carry on from where this stopped.";

    return (
      <SubStepShell
        title="Go live"
        description={description}
        thenWhat={thenWhat}
        onContinue={() => void goNext()}
        continueDisabled
      >
        <GroupedSequence
          title={`Taking ${name} live`}
          steps={rows}
          onRetry={failed && !running ? () => void launch() : undefined}
          onResume={!running ? () => void launch() : undefined}
        />
        {refusal && <p className="text-sm text-destructive">{refusal}</p>}
      </SubStepShell>
    );
  }

  // ---- Face 1: review ----
  const ready = Boolean(client.subdomain) && Boolean(client.accountStartDate);

  return (
    <SubStepShell
      title="Review"
      description={`Everything we've set up for ${name} - confirm and go live below.`}
      thenWhat={`Confirm and go live → we provision ${state?.subdomain || "the tenant"}, add everyone, and ${name} goes live.`}
      onContinue={() => void launch()}
      continueLabel="Confirm and go live"
      continueDisabled={!ready || state === undefined}
    >
      <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 px-4 py-3 text-sm text-amber-900">
        Going live is a one-way door: it provisions the tenant, creates the company and every login, and
        hands over the Zoho connection. The account start date anchors opening balances and compliance
        from here on.
      </div>

      <div className="flex flex-col gap-4 rounded-lg border p-5">
        <div className="flex flex-col gap-1 border-b pb-3">
          <h3 className="text-base font-semibold tracking-tight">Setup summary</h3>
          <p className="text-sm text-muted-foreground">
            {name} - {client.entityType.replace(/-/g, " ")}
          </p>
        </div>

        <SummaryRow label="Tenant address">
          {client.subdomain ? (
            <span className="font-mono text-[13px]">{state?.app_url ?? `${client.subdomain}.initium.zournalhq.com`}</span>
          ) : (
            <span className="text-destructive">No subdomain - set one on Company details.</span>
          )}
        </SummaryRow>

        <SummaryRow label="Account start date">
          {client.accountStartDate || <span className="text-destructive">Not set - pick one on the Start date step.</span>}
        </SummaryRow>

        <SummaryRow label="Team">
          {team && team.length > 0 ? (
            <ul className="flex flex-col gap-0.5">
              {team.map((member) => (
                <li key={member.name}>
                  <span className="font-medium">{member.name}</span>{" "}
                  <span className="text-muted-foreground">· {member.roleLabel}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted-foreground">No team members on file.</span>
          )}
        </SummaryRow>

        <SummaryRow label="Approvals">
          {approvals?.chosen ? (
            <span>
              {approvals.packageLabel}
              <span className="text-muted-foreground"> · {approvals.queue} actions wait for a reviewer</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Not chosen yet.</span>
          )}
        </SummaryRow>

        <SummaryRow label="Escalation contacts">
          {escalations && escalations.escalation.length > 0 ? (
            <span>
              {escalations.escalation.join(", ")}
              <span className="text-muted-foreground">
                {" "}
                · {escalations.dashboardCount} dashboard {escalations.dashboardCount === 1 ? "login" : "logins"}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">No escalation contacts yet.</span>
          )}
        </SummaryRow>
      </div>

      {refusal && <p className="text-sm text-destructive">{refusal}</p>}
    </SubStepShell>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="text-sm">{children}</div>
    </section>
  );
}

/**
 * The permanent record: how to get into the books, and who can get in. Firm
 * users are deliberately absent — they enter through this dashboard, not the
 * tenant's login page.
 *
 * AND THE TENANT'S OWN ADDRESS IS DELIBERATELY ABSENT TOO. It used to be the
 * first thing here, as a link, and it sent firm users to a login page that will
 * not have them: they sign in to master admin and pick the firm. Printing the
 * address also meant printing a different one before and after go-live — a
 * guessed `sub.initium…` while provisioning, the stored `app_url` afterwards —
 * which read as the URL having changed. So the panel says how to get in instead
 * of where the tenant is, and the address is left to the places that are about
 * the tenant rather than about the person reading.
 */
function LivePanel({
  state,
  name,
  masterAdminDomain,
}: {
  state: GoLiveStateDTO;
  name: string;
  /** This browser's origin, e.g. https://master.notion.zournalhq.com — no path. */
  masterAdminDomain: string;
}) {
  return (
    <div className="flex flex-col gap-5 rounded-lg border p-5">
      <section className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Opening the books</span>
        <p className="text-sm">
          To open {name}'s books, log out, sign in to master admin at{" "}
          <span className="font-mono text-[13px]">{masterAdminDomain}</span> and select the firm.
        </p>
      </section>

      {state.panel.admin && (
        <section className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Company admin</span>
          <p className="font-mono text-[13px]">{state.panel.admin.email}</p>
          <p className="font-mono text-[13px]">{state.panel.admin.password}</p>
          <p className="text-xs text-muted-foreground">Must change the password at first login.</p>
        </section>
      )}

      {state.panel.viewers.length > 0 && (
        <section className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Client dashboard logins</span>
          <ul className="flex flex-col gap-1">
            {state.panel.viewers.map((viewer) => (
              <li key={viewer.email} className="text-sm">
                <span className="font-medium">{viewer.name}</span>{" "}
                <span className="font-mono text-[13px] text-muted-foreground">· {viewer.email}</span>
              </li>
            ))}
          </ul>
          {state.panel.viewer_password && (
            <p className="text-xs text-muted-foreground">
              Initial password <span className="font-mono">{state.panel.viewer_password}</span> - anyone who
              already had a Zournal account keeps their own.
            </p>
          )}
        </section>
      )}

      <section className="flex flex-col gap-1 border-t pt-4">
        <span className="text-xs font-medium text-muted-foreground">Still by hand, in the tenant</span>
        <p className="text-sm text-muted-foreground">
          A non-standard approval package, the month-close template, and compliance types - plus a bank
          account number wherever Zoho holds none (the bank row above says which). The full runbook lives
          in docs/onboarding-tenant-data-map.md.
        </p>
      </section>
    </div>
  );
}

function formatStamp(iso: string | null): string {
  if (!iso) return "just now";
  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}
